import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, xcss } from '@atlaskit/primitives';
import {
  ColGroup,
  CustomDynamicResultTable,
  FormattedDate,
  TableBody,
  TableHead,
  useApplicationContext,
} from '@css-xp/base';
import Button from '@atlaskit/button';
import { genericEmptyStateProp, NoData } from '@css-xp/ppc-common';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { fetchDealRegistrationsByPartnerId } from '../queries/fetch-deal-registrations-by-partner-id';
import { DEAL_REGISTRATIONS_CONSTANTS } from '@css-xp/ppc-constants';
import { DEAL_REGISTRATIONS_TYPES } from '@css-xp/ppc-types';
import './deal-registrations-table.scss';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { useParams } from 'react-router-dom';

export const DealRegistrationsTable = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const partnerData = useApplicationContext();
  const selectedPartnerAccountData = partnerData.partnerAccounts.find(
    ({ transactionAccountId }: { transactionAccountId: string }) => transactionAccountId === txaId
  );

  const partnerSalesforceId = selectedPartnerAccountData?.id;

  const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);
  const { loading, error, data } = useQuery(fetchDealRegistrationsByPartnerId, {
    variables: {
      options: { partnerAccountIds: [partnerSalesforceId] },
      pageParams: {
        first: 8,
        offset: 0,
      },
    },
    skip: !partnerSalesforceId,
    // @ts-ignore
    client: ppcBffGraphqlClient,
  });

  const dealRegistrations = (data?.dealRegistrations?.nodes || []) as DEAL_REGISTRATIONS_TYPES.DealRegistration[];

  const getRowData = (dealRegistration: DEAL_REGISTRATIONS_TYPES.DealRegistration) => {
    return DEAL_REGISTRATIONS_CONSTANTS.HEADERS.map((column) => {
      switch (column.name) {
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.REGISTRATION_NAME:
          return <td>{dealRegistration.registrationName}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.PROGRAM_NAME:
          return <td>{dealRegistration.programName}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.TYPE_OF_DEAL:
          return <td>{dealRegistration.typeOfDeal}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.LINKED_CUSTOMER_ACCOUNT:
          return <td>{dealRegistration.linkedCustomerAccount}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.ATLASSIAN_SALES_REP:
          return <td>{dealRegistration.atlassianSalesRep}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.ATLASSIAN_SSE_REP:
          return <td>{dealRegistration.atlassianSolutionSalesExecRep}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.REGISTRATION_STATUS:
          return <td>{dealRegistration.registrationStatus}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.REGISTRATION_DEAL_ID:
          return <td>{dealRegistration.dealId}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.GSAC_TICKET:
          return <td>{dealRegistration.gsacTicket}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.REGISTRATION_APPROVAL_ID:
          return <td>{dealRegistration.approvalId}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.REGISTRATION_SUBMITTED_DATE:
          return (
            <td>
              <FormattedDate date={dealRegistration.dateOfSubmission} format={NEW_DATE_FORMAT} />
            </td>
          );
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.REGISTRATION_EXPIRATION_DATE:
          return (
            <td>
              <FormattedDate date={dealRegistration.dateOfExpiration} format={NEW_DATE_FORMAT} />
            </td>
          );
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.DENIAL_REASON:
          return <td>{dealRegistration.denialReason}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.COMMENTS:
          return <td>{dealRegistration.comments}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.SOW_AND_PO_AMOUNT:
          return <td>{`${dealRegistration.statementOfWorkAmount} / ${dealRegistration.purchaseOrderAmount}`}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.EXTENSION_STATUS:
          return <td>{dealRegistration.extensionStatus ?? 'N/A'}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.QUOTE_ID:
          return <td>{}</td>;
        case DEAL_REGISTRATIONS_CONSTANTS.COLUMNS.ACTION:
          return (
            <td>
              <Button appearance="default" testId="deal-reg-create-quote-btn">
                Create quote
              </Button>
            </td>
          );
      }
    });
  };

  const getRowsWithData = () => (
    <>
      <ColGroup columns={DEAL_REGISTRATIONS_CONSTANTS.HEADERS} />
      <TableHead columns={DEAL_REGISTRATIONS_CONSTANTS.HEADERS} />
      <TableBody>
        {dealRegistrations.map((dealRegistration: DEAL_REGISTRATIONS_TYPES.DealRegistration, index: number) => {
          return <tr key={dealRegistration?.dealId ?? index.toString()}>{getRowData(dealRegistration)}</tr>;
        })}
      </TableBody>
    </>
  );

  return (
    <Box>
      <CustomDynamicResultTable
        className="deal-registrations-table"
        noResults={false}
        isLoading={loading}
        error={''}
        emptyStateProps={genericEmptyStateProp}
        showLoadMore={true}
        hasTableWrapper={true}
        testId="deal-registrations-table"
      >
        {getRowsWithData()}
      </CustomDynamicResultTable>
    </Box>
  );
};
